import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  Users, 
  Shield, 
  Zap, 
  DollarSign, 
  Phone, 
  BarChart3, 
  CheckCircle,
  Star,
  Clock,
  Percent,
  Trophy,
  CreditCard,
  Store,
  AlertCircle,
  HandHeart,
  Target,
  BadgeCheck
} from 'lucide-react';

const WhyDailzySaathi = () => {
  const reasons = [
    {
      icon: DollarSign,
      title: "Zero Setup Cost",
      description: "Start selling with no upfront investment. We provide everything you need to begin your digital journey.",
      stat: "₹0",
      statLabel: "Setup cost",
      color: "bg-green-50 text-green-600 border-green-200",
      iconColor: "text-green-500"
    },
    {
      icon: Percent,
      title: "Minimal Commission",
      description: "Keep more of what you earn with our industry-lowest commission rates. Fair pricing for fair partnerships.",
      stat: "2-5%",
      statLabel: "Commission only",
      color: "bg-blue-50 text-blue-600 border-blue-200",
      iconColor: "text-blue-500"
    },
    {
      icon: Zap,
      title: "Instant Payment Settlement",
      description: "Get paid instantly after each delivery. No waiting periods, no delays - your money, your way.",
      stat: "Instant",
      statLabel: "Payment settlement",
      color: "bg-purple-50 text-purple-600 border-purple-200",
      iconColor: "text-purple-500"
    },
    {
      icon: TrendingUp,
      title: "Guaranteed Business Growth",
      description: "Access to 500K+ customers in your area. Increase your sales by 3-5x with our proven platform.",
      stat: "3-5x",
      statLabel: "Sales increase",
      color: "bg-orange-50 text-orange-600 border-orange-200",
      iconColor: "text-orange-500"
    },
    {
      icon: Phone,
      title: "24/7 Vendor Support",
      description: "Dedicated support team for all your business needs. We're here to help you succeed, anytime.",
      stat: "24/7",
      statLabel: "Vendor support",
      color: "bg-red-50 text-red-600 border-red-200",
      iconColor: "text-red-500"
    },
    {
      icon: BarChart3,
      title: "Real-time Analytics",
      description: "Track your sales, understand customer behavior, and optimize your business with detailed insights.",
      stat: "Live",
      statLabel: "Business insights",
      color: "bg-indigo-50 text-indigo-600 border-indigo-200",
      iconColor: "text-indigo-500"
    },
    {
      icon: HandHeart,
      title: "Marketing Support",
      description: "Get featured in our promotional campaigns and social media. We help you reach more customers.",
      stat: "Low Cost",
      statLabel: "Marketing support",
      color: "bg-pink-50 text-pink-600 border-pink-200",
      iconColor: "text-pink-500"
    },
    {
      icon: Target,
      title: "Hyperlocal Reach",
      description: "Connect with customers in your immediate vicinity. Perfect for local businesses wanting to go digital.",
      stat: "Local",
      statLabel: "Customer targeting",
      color: "bg-teal-50 text-teal-600 border-teal-200",
      iconColor: "text-teal-500"
    }
  ];

  const achievements = [
    { number: "15K+", label: "Happy Vendors", icon: Users },
    { number: "50+", label: "Cities Covered", icon: Store },
    { number: "2.5M+", label: "Orders Delivered", icon: CheckCircle },
    { number: "98%", label: "Vendor Satisfaction", icon: Star }
  ];

  const testimonialHighlights = [
    { vendor: "Raj Electronics", growth: "+400%", period: "6 months" },
    { vendor: "Sharma Grocers", growth: "+350%", period: "8 months" },
    { vendor: "Medical Plus", growth: "+500%", period: "4 months" },
    { vendor: "Fresh Mart", growth: "+380%", period: "7 months" }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-white via-gray-50 to-red-50 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-red-200 to-orange-200 rounded-full opacity-10 blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-orange-200 to-red-200 rounded-full opacity-10 blur-3xl translate-x-1/2 translate-y-1/2"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div 
            className="inline-flex items-center gap-2 bg-gradient-to-r from-red-50 to-orange-50 text-red-500 rounded-2xl px-4 py-2 mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Trophy size={20} />
            <span className="font-semibold text-sm">Vendor Benefits</span>
          </motion.div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
            Why Choose <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Dailzy Saathi?</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Join India's fastest-growing vendor network and transform your local business into a digital powerhouse. 
            Here's why 15K+ vendors trust Dailzy Saathi.
          </p>
        </motion.div>

        {/* Main reasons grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              className={`bg-white rounded-2xl p-8 border-2 ${reason.color} hover:shadow-xl transition-all duration-300 group relative overflow-hidden`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              {/* Background gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-transparent to-gray-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              <div className="relative z-10">
                {/* Icon and stat */}
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-16 h-16 rounded-2xl bg-white border-2 flex items-center justify-center ${reason.color} group-hover:scale-110 transition-transform duration-300`}>
                    <reason.icon className={`w-8 h-8 ${reason.iconColor}`} />
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${reason.iconColor}`}>{reason.stat}</div>
                    <div className="text-xs text-gray-500">{reason.statLabel}</div>
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-gray-900 transition-colors">
                  {reason.title}
                </h3>
                <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors">
                  {reason.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>



        {/* Achievements */}
        {/* <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-gray-800 mb-8">
            Our Vendor Network Achievements
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto mb-12">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 group"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <achievement.icon className="w-8 h-8 text-red-500 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <div className="text-3xl font-bold text-gray-800 mb-2">{achievement.number}</div>
                <div className="text-sm text-gray-600">{achievement.label}</div>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-red-500 via-red-600 to-orange-500 rounded-2xl p-8 text-white max-w-3xl mx-auto">
              <h4 className="text-2xl font-bold mb-4">Ready to Join the Dailzy Saathi Family?</h4>
              <p className="text-lg opacity-90 mb-6">
                Join 15K+ successful vendors who have transformed their businesses with Dailzy Saathi. 
                Start your digital journey today with zero setup cost.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/vendors#vendor-registration" className="bg-white text-red-600 px-8 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center">
                  <Store className="w-5 h-5 mr-2" />
                  Start Selling Now
                </Link>
                <Link to="/contact" className="border-2 border-white text-white px-8 py-3 rounded-xl font-semibold hover:bg-white/10 transition-colors flex items-center justify-center">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Support
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default WhyDailzySaathi; 